import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { runOnNextTick } from '@rallycommerce/common/utils.js';
import { distinctUntilChanged } from 'rxjs/operators';
import { generateUniqueAdminId } from '../../core/helpers/utils';
import { SmartEncodingService } from '../../core/smart-encoding.service';
import { SessionService } from '../../core/session.service';

@Component({
  selector: 'rosie-text',
  templateUrl: './text.component.html',
  styleUrls: ['./text.component.scss'],
  standalone: false
})
export class TextComponent implements OnInit, AfterViewInit {
  @Input() id = generateUniqueAdminId();
  @Input() disabled = false;
  @Input() subType = TextSubtype.Text;
  @Input() placeholderTranslationKey = '';
  @Input() labelTranslationKey = '';
  @Input() control: UntypedFormControl | AbstractControl;
  @Input() type = TextType.Input;
  @Input() rows = 13;
  @Input() prefix: string;
  @Input() alwaysDisplayPrefix = false;
  @Input() lowercaseOnly = false;
  @Input() customClass = '';
  @Input() min: number = null;
  @Input() maxLength: number = null;
  @Input() tooltip = '';
  @Input() hasSmartEncodingEnabled: boolean = false;
  @Input() autoExpand: boolean = true;
  @Output() onFocusChanged = new EventEmitter<Event>();
  @Output() onBlur = new EventEmitter<Event>();
  @Output() onEnter = new EventEmitter<Event>();

  @ViewChild('prefixContainer', { read: ElementRef }) prefixEl: ElementRef;
  @ViewChild('inputElement', { read: ElementRef }) inputElement: ElementRef;
  @ViewChild('textareaElement', { read: ElementRef }) textareaElement: ElementRef;
  @ViewChild('autoExpandInput', { read: ElementRef }) autoExpandInput: ElementRef;

  inputPaddingLeft?: string;
  constructor(private encodingService: SmartEncodingService, private session: SessionService) { }

  get hasValue(): boolean {
    return this.control.value !== null && this.control.value !== undefined && this.control.value !== '';
  }

  get isInputType(): boolean {
    return this.type !== TextType.Area;
  }

  get hasTooltip(): boolean {
    return !!this.tooltip;
  }

  get isUrlInputType(): boolean {
    return this.type === TextType.Url;
  }

  get isEmailInputType(): boolean {
    return this.type === TextType.Email;
  }

  get isNumberInputType(): boolean {
    return this.type === TextType.Number;
  }

  get inputMode(): string {
    switch (this.type) {
      case TextType.Email:
        return 'email';
      case TextType.Number:
        return 'numeric';
      case TextType.Url:
        return 'url';
      default:
        return 'text';
    }
  }

  get inputType(): string {
    return this.type;
  }

  ngOnInit(): void {
    if (this.control) {
      if (this.lowercaseOnly) {
        this.control.valueChanges
          .pipe(distinctUntilChanged())
          .subscribe(value => {
            this.control.patchValue(value?.toLowerCase(), { emitEvent: false });
          });
      }

      this.control.valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
        if (value && this.hasSmartEncodingEnabled) {
          const encodedValue = this.encodingService.encodeText(value);
          if (encodedValue !== value) {
            this.control.patchValue(encodedValue, { emitEvent: false });
          }
        }

        if (this.shouldUseAutoExpand()) {
          runOnNextTick(() => this.adjustTextareaHeight());
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.prefix && this.prefixEl) {
      runOnNextTick(() => {
        this.inputPaddingLeft = `calc(0.875rem + ${this.prefixEl.nativeElement.offsetWidth || 9}px + 5px)`;
      });
    }

    if (this.shouldUseAutoExpand()) {
      runOnNextTick(() => this.adjustTextareaHeight());
    }
  }

  handleOnFocus(event: Event) {
    this.control.markAsUntouched();
    this.onFocusChanged.next(event);
    this.scrollIntoViewIfNeeded();

    if (this.shouldUseAutoExpand()) {
      runOnNextTick(() => this.adjustTextareaHeight());
    }
  }

  handleOnBlur(event: Event) {
    this.onFocusChanged.next(event);
    this.onBlur.next(event);
  }

  handleOnEnter(event?: Event) {
    if (this.shouldUseAutoExpand() && event) {
      event.preventDefault();
      return;
    }
    this.onEnter.next(event);
  }

  handleInput() {
    if (this.shouldUseAutoExpand()) {
      this.adjustTextareaHeight();
    }
  }

  private shouldUseAutoExpand(): boolean {
    return this.autoExpand !== false && !this.isEmailInputType && !this.isNumberInputType && !this.isUrlInputType;
  }

  private adjustTextareaHeight() {
    if (!this.autoExpandInput) {
      return;
    }

    const textarea = this.autoExpandInput.nativeElement;
    textarea.style.height = '44px';

    if (textarea.scrollHeight > 44) {
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }

  private scrollIntoViewIfNeeded(): void {
    if (this.isMobileDevice()) {
      setTimeout(() => {
        const element = this.autoExpandInput?.nativeElement || this.inputElement?.nativeElement || this.textareaElement?.nativeElement;
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 300);
    }
  }

  private isMobileDevice(): boolean {
    return this.session.isMobileVersion;
  }
}

export enum TextType {
  Input = 'input',
  Area = 'textarea',
  Number = 'number',
  Text = 'text',
  Url = 'url',
  Email = 'email',
}

export enum TextSubtype {
  Text = 'text',
}

export enum DiscountUnit {
  Percentage = '%',
  Dollar = '$'
}

export enum EventType {
  Focus = 'focus'
}

